import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { fmtMoney, fmtDate } from '../lib/fmt';
import { isoDate } from '../lib/dateHelpers';
import { openPdfFromApi } from '../lib/pdf';
import type { LogisticaCalendarioOut, LogisticaCalendarioPedidoOut } from '../types/api';

const STATUS_COLOR: Record<string, string> = {
  'Agendado':               'bg-mbc-blue/25 text-mbc-bluel border-mbc-blue/30',
  'Em trânsito':            'bg-sky-500/20 text-sky-300 border-sky-500/30',
  'Recebido parcial':       'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  'Recebido total':         'bg-emerald-500/35 text-emerald-100 border-emerald-500/40',
  'Recebido':               'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  'Aguardando agendamento': 'bg-amber-500/15 text-amber-300 border-amber-500/25',
};

const DAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const CHIPS_POR_DIA = 3;

function statusColor(pedido: LogisticaCalendarioPedidoOut): string {
  const s = pedido.status_log ?? 'Aguardando agendamento';
  return STATUS_COLOR[s] ?? 'bg-slate-500/20 text-slate-300 border-slate-500/20';
}

function buildCalendarGrid(viewMonth: Date): Date[] {
  const start = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const startOffset = start.getDay();
  const gridStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1 - startOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
}

function PedidoChip({ p, onClick }: { p: LogisticaCalendarioPedidoOut; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${p.numero} · ${p.fornecedor} · ${fmtMoney(p.valor_total)}`}
      className={`flex w-full items-center gap-1 truncate rounded border px-1.5 py-0.5 text-left text-[10px] leading-tight transition-opacity hover:opacity-80 ${statusColor(p)}`}
    >
      <span className="font-mono font-semibold shrink-0">{p.numero}</span>
      <span className="truncate opacity-70">{p.fornecedor}</span>
    </button>
  );
}

function PedidoDetailModal({
  pedido,
  onClose,
}: {
  pedido: LogisticaCalendarioPedidoOut;
  onClose: () => void;
}) {
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const openPdf = async () => {
    setPdfErr(null);
    setLoadingPdf(true);
    try {
      await openPdfFromApi(`/pedidos/${pedido.id}/pdf`, `${pedido.numero}.pdf`);
    } catch (e: any) {
      setPdfErr(e?.message || e?.response?.data?.detail || `PDF não encontrado para o pedido ${pedido.numero}.`);
    } finally {
      setLoadingPdf(false);
    }
  };

  const statusLog = pedido.status_log ?? 'Aguardando agendamento';

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-end md:place-items-center p-2 md:p-4 pointer-events-none">
        <div className="card glow pointer-events-auto w-full max-w-lg overflow-y-auto rounded-2xl p-4 md:p-6" style={{ maxHeight: '96vh' }}>
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-extrabold text-white">
                📦 Pedido <span className="font-mono">{pedido.numero}</span>
              </h2>
              <p className="mt-1 text-sm text-mbc-soft">{pedido.fornecedor}</p>
            </div>
            <button type="button" onClick={onClose} className="text-3xl leading-none text-mbc-mute hover:text-white">×</button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-mbc-line bg-mbc-panel2 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-mbc-mute">Valor total</p>
              <p className="mt-0.5 text-sm font-bold text-white">{fmtMoney(pedido.valor_total)}</p>
            </div>
            <div className="rounded-xl border border-mbc-line bg-mbc-panel2 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-mbc-mute">Entrega agendada</p>
              <p className="mt-0.5 text-sm font-bold text-white">{fmtDate(pedido.data_entrega_agendada)}</p>
            </div>
            <div className="rounded-xl border border-mbc-line bg-mbc-panel2 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-mbc-mute">Status logística</p>
              <p className="mt-1">
                <span className={`inline-flex rounded border px-2 py-0.5 text-xs font-semibold ${statusColor(pedido)}`}>{statusLog}</span>
              </p>
            </div>
            <div className="rounded-xl border border-mbc-line bg-mbc-panel2 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-mbc-mute">Status pedido</p>
              <p className="mt-0.5 text-sm font-semibold text-mbc-soft">{pedido.status_po}</p>
            </div>
            {pedido.dias_para_entrega != null && (
              <div className="col-span-2 rounded-xl border border-mbc-line bg-mbc-panel2 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-mbc-mute">Dias para entrega</p>
                <p className="mt-0.5 text-sm font-bold text-white">
                  {pedido.dias_para_entrega === 0
                    ? 'Hoje'
                    : pedido.dias_para_entrega > 0
                      ? `Em ${pedido.dias_para_entrega} dia(s)`
                      : `${Math.abs(pedido.dias_para_entrega)} dia(s) atrás`}
                </p>
              </div>
            )}
          </div>

          {pdfErr && (
            <div className="mt-4 rounded-lg border border-rose-500/25 bg-rose-500/10 px-4 py-2 text-sm text-rose-300">
              {pdfErr}
            </div>
          )}

          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-mbc-line bg-mbc-panel2 px-5 py-2.5 text-mbc-soft hover:text-white w-full sm:w-auto">
              Fechar
            </button>
            <button type="button" onClick={openPdf} disabled={loadingPdf} className="pill-btn rounded-xl bg-mbc-orange px-6 py-2.5 font-bold text-white shadow-lg hover:bg-mbc-orangel disabled:opacity-40 w-full sm:w-auto">
              {loadingPdf ? 'Abrindo…' : '📄 Visualizar PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function CalendarioLogistica() {
  const { data, isLoading } = useQuery<LogisticaCalendarioOut>({
    queryKey: ['logistica-calendario'],
    queryFn: () => api.get<LogisticaCalendarioOut>('/pedidos/logistica/calendario').then(r => r.data),
    staleTime: 30_000,
  });

  const now = new Date();
  const [cursor, setCursor] = useState<{ y: number; m: number }>({ y: now.getFullYear(), m: now.getMonth() });
  const [selecionado, setSelecionado] = useState<LogisticaCalendarioPedidoOut | null>(null);

  const porDia = useMemo(() => {
    const map = new Map<string, LogisticaCalendarioPedidoOut[]>();
    if (!data) return map;
    const seen = new Set<number>();
    for (const s of data.semanas) {
      for (const p of s.pedidos) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        const key = p.data_entrega_agendada.slice(0, 10);
        const bucket = map.get(key);
        if (bucket) bucket.push(p);
        else map.set(key, [p]);
      }
    }
    return map;
  }, [data]);

  if (isLoading) {
    return <div className="p-10 text-center text-mbc-mute">Carregando calendário…</div>;
  }

  if (!data || data.semanas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-4xl">📅</p>
        <p className="mt-3 text-sm font-semibold text-mbc-soft">Nenhuma entrega agendada</p>
        <p className="mt-1 text-xs text-mbc-mute">Agende a data de entrega nos pedidos aprovados para visualizá-los aqui.</p>
      </div>
    );
  }

  const viewMonth = new Date(cursor.y, cursor.m, 1);
  const cells = buildCalendarGrid(viewMonth);
  const todayIso = isoDate(now);
  const goPrev = () => setCursor(c => { const d = new Date(c.y, c.m - 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const goNext = () => setCursor(c => { const d = new Date(c.y, c.m + 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const goToday = () => setCursor({ y: now.getFullYear(), m: now.getMonth() });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button type="button" onClick={goPrev} className="rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-1.5 text-mbc-soft hover:text-white">‹</button>
          <div className="min-w-[160px] text-center text-sm font-bold text-white">
            {MONTHS[viewMonth.getMonth()]} de {viewMonth.getFullYear()}
          </div>
          <button type="button" onClick={goNext} className="rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-1.5 text-mbc-soft hover:text-white">›</button>
        </div>
        <button type="button" onClick={goToday} className="rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-1.5 text-xs font-semibold text-mbc-soft hover:text-white">Hoje</button>
      </div>

      <div>
        <div className="grid grid-cols-7 gap-px text-center text-[11px] font-bold text-mbc-soft/85">
          {DAYS.map(d => <div key={d} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl bg-mbc-line">
          {cells.map(date => {
            const iso = isoDate(date);
            const inMonth = date.getMonth() === viewMonth.getMonth();
            const isToday = iso === todayIso;
            const pedidos = porDia.get(iso) ?? [];
            const extra = pedidos.length - CHIPS_POR_DIA;
            return (
              <div
                key={iso}
                className={`flex min-h-[92px] flex-col gap-1 p-1.5 ${inMonth ? 'bg-mbc-panel' : 'bg-mbc-panel2/40'}`}
              >
                <div className="flex justify-end">
                  <span className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold ${
                    isToday ? 'bg-mbc-orange text-white' : inMonth ? 'text-mbc-soft' : 'text-mbc-mute'
                  }`}>
                    {date.getDate()}
                  </span>
                </div>
                <div className="flex flex-col gap-1 overflow-hidden">
                  {pedidos.slice(0, CHIPS_POR_DIA).map(p => (
                    <PedidoChip key={p.id} p={p} onClick={() => setSelecionado(p)} />
                  ))}
                  {extra > 0 && (
                    <span className="text-[10px] font-semibold text-mbc-mute">+{extra}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selecionado && (
        <PedidoDetailModal pedido={selecionado} onClose={() => setSelecionado(null)} />
      )}
    </div>
  );
}
